import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CircleCalendar.css';

const localizer = momentLocalizer(moment);

const MEMBER_COLORS = ['#1B2B4B','#E8654A','#10B981','#3B82F6','#8B5CF6','#F59E0B','#EF4444'];

const CircleCalendar = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [circle, setCircle] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState(Views.WEEK);
  const [date, setDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [memberColorMap, setMemberColorMap] = useState({});

  const authHeaders = {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  };

  const fetchData = useCallback(async () => {
    try {
      const [circleRes, eventsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/circles/mine`, { headers: authHeaders }),
        fetch(`${process.env.REACT_APP_API_URL}/api/circles/${id}/events`, { headers: authHeaders }),
      ]);

      if (circleRes.ok) {
        const data = await circleRes.json();
        setCircle(data.circle);

        // Build color map — each member gets a consistent color
        const colorMap = {};
        data.circle?.members?.forEach((m, i) => {
          colorMap[m.userId?._id || m.userId] = MEMBER_COLORS[i % MEMBER_COLORS.length];
        });
        setMemberColorMap(colorMap);
      }

      if (eventsRes.ok) {
        const data = await eventsRes.json();
        const formatted = (data.events || []).map(evt => ({
          ...evt,
          id: evt._id,
          title: evt.title,
          start: new Date(evt.startTime),
          end: new Date(evt.endTime),
          resource: evt,
        }));
        setEvents(formatted);
      } else {
        const err = await eventsRes.json();
        setError(err.error || 'Failed to load events.');
      }
    } catch (err) {
      console.error('[CircleCalendar]', err);
      setError('Failed to load Circle calendar.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const eventPropGetter = (event) => {
    const creatorId = event.resource?.createdBy?._id || event.resource?.createdBy;
    const color = memberColorMap[creatorId] || '#1B2B4B';
    return {
      style: {
        backgroundColor: color,
        color: '#fff',
        borderRadius: '6px',
        border: 'none',
        fontSize: '0.82rem',
      },
    };
  };

  const handleSelectEvent = (event) => setSelectedEvent(event);

  if (loading) return (
    <div className="circle-page">
      <div className="circle-container">
        <div className="circle-loading">Loading Circle calendar...</div>
      </div>
    </div>
  );

  return (
    <div className="circle-cal-page">
      {/* Nav */}
      <div className="circle-cal-nav">
        <button className="back-to-dashboard-btn" onClick={() => navigate(`/circles/${id}/manage`)}>
          ← Circle
        </button>
        <button className="back-to-dashboard-btn" onClick={() => navigate('/dashboard')}>
          Dashboard
        </button>
        <Link to="/calendar" className="back-to-dashboard-btn">
          My Calendar
        </Link>
      </div>

      {/* Header */}
      <div className="circle-cal-header">
        <div className="circle-sparkle" style={{fontSize:'1.4rem',color:'#E8654A'}}>✦</div>
        <h1>{circle?.name || 'Circle'} Calendar</h1>
        <p>Shared events from all Circle members</p>
      </div>

      {/* Member legend */}
      {circle?.members && (
        <div className="circle-cal-legend">
          {circle.members.map((m, i) => {
            const u = m.userId;
            const color = MEMBER_COLORS[i % MEMBER_COLORS.length];
            return (
              <div key={m._id || i} className="circle-cal-legend-item">
                <div className="circle-cal-legend-dot" style={{ background: color }} />
                <span>{u?.firstname} {u?.lastname}</span>
              </div>
            );
          })}
        </div>
      )}

      {error && <div className="circle-error" style={{marginBottom:'1rem'}}>⚠️ {error}</div>}

      {/* Calendar */}
      <div className="circle-cal-wrapper">
        <Calendar
          localizer={localizer}
          events={events}
          view={view}
          date={date}
          onView={setView}
          onNavigate={setDate}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventPropGetter}
          style={{ height: 600 }}
          views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
          popup
        />
      </div>

      {/* Event detail modal */}
      {selectedEvent && (
        <div className="circle-cal-modal-overlay" onClick={() => setSelectedEvent(null)}>
          <div className="circle-cal-modal" onClick={e => e.stopPropagation()}>
            <button className="circle-cal-modal-close" onClick={() => setSelectedEvent(null)}>✕</button>
            <h2>{selectedEvent.title}</h2>
            <div className="circle-cal-modal-detail">
              <span>📅</span>
              <span>{moment(selectedEvent.start).format('ddd, MMM D · h:mm A')} – {moment(selectedEvent.end).format('h:mm A')}</span>
            </div>
            {selectedEvent.resource?.creatorName && (
              <div className="circle-cal-modal-detail">
                <span>👤</span>
                <span>{selectedEvent.resource.creatorName}</span>
              </div>
            )}
            {selectedEvent.resource?.clientId && (
              <div className="circle-cal-modal-detail">
                <span>🧑‍💼</span>
                <Link
                  to={`/clientdashboard/${selectedEvent.resource.clientId._id || selectedEvent.resource.clientId}`}
                  onClick={() => setSelectedEvent(null)}
                  style={{color:'var(--accent)',fontWeight:600}}
                >
                  {selectedEvent.resource.clientId.firstName} {selectedEvent.resource.clientId.lastName}
                </Link>
              </div>
            )}
            {selectedEvent.resource?.location && (
              <div className="circle-cal-modal-detail">
                <span>📍</span>
                <span>{selectedEvent.resource.location}</span>
              </div>
            )}
            {selectedEvent.resource?.description && (
              <div className="circle-cal-modal-detail">
                <span>📝</span>
                <span>{selectedEvent.resource.description}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CircleCalendar;
